import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Box, Button, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, TextField, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { duplicateAttributeNames } from '../../utils/attributeChanges';

const TYPE_OPTIONS = ['string', 'number', 'boolean', 'object'];

// Generate a default attribute name that doesn't collide with an existing row,
// so two quick "Add"s don't silently overwrite each other on save (T1.4b).
const uniqueDefaultName = (attributes) => {
  const taken = new Set((attributes || []).map((a) => a && a.name));
  if (!taken.has('New Attribute')) return 'New Attribute';
  let n = 2;
  while (taken.has(`New Attribute ${n}`)) n += 1;
  return `New Attribute ${n}`;
};

const AttributesTab = ({ attributes, setAttributes }) => {
  // #654 (1): flag every row whose name collides with another row's — computed
  // fresh on each render straight from the live `attributes` prop, so it's
  // always in sync with what a Save would actually produce (no separate state
  // to drift out of step with a rename/delete).
  const dupes = duplicateAttributeNames(attributes);

  const handleAddAttribute = () => {
    // #943: `_key` is a row-identity field for React's `key=` only — ItemDetails.js
    // stamps the same field onto every row it loads via attributesToRows(...), and
    // rowsToAttributes(...)/serializeAttributeRows(...) both read a fixed whitelist
    // of fields, so an extra `_key` never reaches the save payload or the
    // dirty-check. Without it, `key={index}` would let a mid-list delete hand this
    // row's <TextField> DOM node (and any in-progress edit/caret position) to the
    // NEXT row instead of removing it.
    const newAttribute = {
      name: uniqueDefaultName(attributes),
      type: 'string',
      value: '',
      structured: false,
      valueIsJson: false,
      _key: uuidv4(),
    };
    setAttributes([...attributes, newAttribute]);
  };

  // Immutable single-field update so name/type/value are all editable inline.
  const handleFieldChange = (index, field, value) => {
    const newAttributes = attributes.map((attr, i) => {
      if (i !== index) return attr;
      // #654 (2): retyping via the dropdown is an explicit override of the
      // original load-time shape — an attribute loaded as structured JSON that
      // the user deliberately retypes to (say) 'string' should actually BECOME
      // a plain string on save, not keep silently re-parsing as JSON. Picking
      // 'object' still means JSON either way, so `valueIsJson` staying stale
      // there is harmless (coerceFromEditString already treats `type==='object'`
      // as JSON regardless of the flag).
      if (field === 'type') return { ...attr, type: value, valueIsJson: false };
      return { ...attr, [field]: value };
    });
    setAttributes(newAttributes);
  };

  const handleDeleteAttribute = (index) => {
    const newAttributes = [...attributes];
    newAttributes.splice(index, 1);
    setAttributes(newAttributes);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={handleAddAttribute}
        size="small"
        sx={{ mb: 1 }}
      >
        Add Attribute
      </Button>
      
      {attributes.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No attributes defined for this item.
        </Typography>
      ) : (
        // #238: a plain MUI <Table> squeezes its columns to fit the container width,
        // which on a narrow phone wraps/overflows the Name/Value/Type cells instead of
        // scrolling — `.scroll-x` (the same pattern used elsewhere, e.g. PlatformConsole)
        // lets the table size to its content and scroll sideways instead.
        <Box className="scroll-x">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Type</TableCell>
                <TableCell padding="none">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attributes.map((attr, index) => {
                const isDupe = attr.name && dupes.has(attr.name);
                return (
                <TableRow key={attr._key || attr.name}>
                  <TableCell>
                    <TextField
                      value={attr.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      size="small"
                      variant="standard"
                      placeholder="name"
                      error={isDupe}
                      // #654 (1): the collision guard — visible BEFORE Save, not
                      // discovered after the fact as a missing attribute.
                      helperText={isDupe ? 'Duplicate name — will overwrite on save' : undefined}
                      slotProps={{
                        htmlInput: {
                          'aria-label': `attribute name ${index}`,
                          'data-testid': `attr-name-${index}`,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={attr.value}
                      onChange={(e) => handleFieldChange(index, 'value', e.target.value)}
                      size="small"
                      variant="standard"
                      placeholder="value"
                      slotProps={{
                        htmlInput: {
                          'aria-label': `attribute value ${index}`,
                          'data-testid': `attr-value-${index}`,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      value={TYPE_OPTIONS.includes(attr.type) ? attr.type : 'string'}
                      onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                      size="small"
                      variant="standard"
                      slotProps={{
                        select: {
                          'aria-label': `attribute type ${index}`,
                          'data-testid': `attr-type-${index}`,
                        },
                      }}
                    >
                      {TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell padding="none">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteAttribute(index)}
                      data-testid={`attr-delete-${index}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

AttributesTab.propTypes = {
  attributes: PropTypes.array.isRequired,
  setAttributes: PropTypes.func.isRequired
};

export default AttributesTab;
